import { useEffect, useMemo } from 'react';
import { useGlobalLoading } from '@/stores/loadingStore';
import type { Application, JobPosition, JobPosting } from '@/types';
import { mergeApplicationsForAnalytics } from '@/lib/applicationArchive';
import { isApplicationsHydrateComplete } from '@/lib/applicationListSync';
import {
  useApplicationsQuery,
  useArchivedApplicationsForAnalyticsQuery,
  usePositionsQuery,
  usePostingsAllQuery,
} from '@/hooks/useRecruiterListQueries';

export interface UseDashboardDataReturn {
  positions: JobPosition[];
  postings: JobPosting[];
  applications: Application[];
  /** True until active progressive hydrate has finished (archived loads in background). */
  applicationsHydrating: boolean;
  loading: boolean;
  error: string | null;
}

export function useDashboardData(companyId: string | undefined): UseDashboardDataReturn {
  const positionsQuery = usePositionsQuery(companyId);
  const postingsQuery = usePostingsAllQuery(companyId);
  const activeApplicationsQuery = useApplicationsQuery(companyId, false);
  const archivedApplicationsQuery = useArchivedApplicationsForAnalyticsQuery(companyId);

  const activeHydrateComplete = companyId
    ? isApplicationsHydrateComplete(companyId, false)
    : true;

  const applicationsHydrating = !!companyId && !activeHydrateComplete;

  // Repair partial legacy caches missing hydrateComplete (not the initial fetch).
  useEffect(() => {
    if (!companyId || activeHydrateComplete || activeApplicationsQuery.isFetching) return;
    if (!activeApplicationsQuery.isFetched) return;
    void activeApplicationsQuery.refetch();
  }, [
    companyId,
    activeHydrateComplete,
    activeApplicationsQuery.isFetching,
    activeApplicationsQuery.isFetched,
    activeApplicationsQuery.refetch,
  ]);

  const applications = useMemo(
    () => mergeApplicationsForAnalytics(
      activeApplicationsQuery.data ?? [],
      archivedApplicationsQuery.data ?? [],
    ),
    [activeApplicationsQuery.data, archivedApplicationsQuery.data],
  );

  const loading = !!companyId && (
    positionsQuery.isLoading
    || postingsQuery.isLoading
    || applicationsHydrating
  );

  const error = useMemo(() => {
    const err = positionsQuery.error
      ?? postingsQuery.error
      ?? activeApplicationsQuery.error;
    if (!err) return null;
    return err instanceof Error ? err.message : 'Failed to load dashboard data';
  }, [
    positionsQuery.error,
    postingsQuery.error,
    activeApplicationsQuery.error,
  ]);

  useGlobalLoading(loading, 'dashboard');

  return {
    positions: positionsQuery.data ?? [],
    postings: postingsQuery.data ?? [],
    applications,
    applicationsHydrating,
    loading,
    error,
  };
}
